import React from 'react';
import { Formik } from 'formik';
import _ from 'lodash';
import { MdKeyboardBackspace, MdAdd, MdSave } from 'react-icons/md';
import * as Yup from 'yup';
import { IconButton } from '~/components/crud/styles';
import DataTable from '~/components/datatable';
import { Autocomplete, InputDate, InputGroup, Select } from '~/components/form';
import useEvents from '~/view-data/use-events';
import useEventCreateOrUpdate from '~/view-data/use-event-create-update';
import { CreateUpdateContainer, FormHeader, Row, TableContainer } from '~/views/events/create-update/styles';
import SupplierModal from '~/views/events/create-update/modal-supplier';
import { formats } from '~/helper';

const EventSchema = Yup.object().shape({
  name: Yup.string().min(4, 'Verifique se o nome do evento está correto').required('Informe o nome do evento'),
  eventType: Yup.mixed().test('', 'Informe o tipo do evento', function(value) {
    const { path, parent } = this
    return !_.isEmpty(_.get(parent, path));
  }),
  place: Yup.mixed().test('', 'Informe o local do evento', function() {
    const { path, parent } = this
    return !_.isEmpty(_.get(parent, path));
  }),
  manager: Yup.mixed().test('', 'Informe o gerente do evento', function() {
    const { path, parent } = this
    return !_.isEmpty(_.get(parent, path));
  }),
  suppliers: Yup.mixed().test('', 'Informe ao menos um fornecedor', function() {
    const { path, parent } = this
    return !_.isEmpty(_.get(parent, path));
  }),
  eventDate: Yup.date().required('Informe a data do evento'),
  eventEndDate: Yup.date().required('Informe a data de término do evento'),
  accreditationStartDate: Yup.date().required('Informe a data início do credenciamento'),
  accreditationEndDate: Yup.date().required('Informe a data final do credenciamento'),
});

const getSuppliers = (previewMode, list, selecteds) => {
  if (!previewMode) {
    return list;
  }
  return _.filter(list, (r) => selecteds.includes(_.get(r, 'id')));
};

function CreateOrUpdate({acls, history}) {
  const { state, actions } = useEventCreateOrUpdate(),
    { actions: eventActions } = useEvents(),
    columns = [
      {
        name: 'CNPJ',
        selector: 'identify',
        width: '200px',
        format: (row) => formats.cnpj_cpf(_.get(row, 'identify'))
      },
      {
        name: 'Fornecedor',
        selector: 'name'
      }
    ];

  const { model = {}, selecteds = [] } = state,
    previewMode = _.get(model, 'status') && _.get(model, 'status') !== 'new',
    isAccreditation = _.get(model, 'status') && _.get(model, 'status') === 'accreditation',
    employeeByCompany = _.countBy(model.employees, 'companyId');

  if (isAccreditation) {
    columns.push({ 
      name: 'Colaboradores',
      selector: 'employeeCount',
      width: '150px',
      right: true,
      format: (row) => formats.number(employeeByCompany[row.id] || 0)
    });
  }
  
  return (
    <CreateUpdateContainer>
      <Formik
        enableReinitialize={true}
        validateOnMount={true}
        validationSchema={EventSchema}
        onSubmit={actions.createOrUpdate}
        initialValues={model}
        initialTouched={{ eventType: true, place: true, manager: true, eventDate: true, eventEndDate: true, accreditationStartDate: true, accreditationEndDate: true }}>
        {({ values, errors, touched, isValid, handleSubmit, ...rest }) => {
          return (
            <div className="contents">
              <div className="form-data">
                <Row span={4}>
                  <InputGroup
                    type="text"
                    name="name"
                    label="Nome"
                    maxLength={150}
                    autoFocus={true}
                    required={true}
                    hideErrorLabel={true}
                    disabled={previewMode}
                  />

                  <Autocomplete
                    name='eventType'
                    minLength={1}
                    keyField='id'
                    required={true}
                    hideErrorLabel={true}
                    label="Tipo de evento"
                    disabled={previewMode}
                    value={values && values.eventType}
                    valueFormat={(row) => `${row.name}`}
                    loadData={actions.listEventsTypes}
                    emptyText={'Pesquise o tipo do evento'}
                    tipText={'Digite... '}
                    loadingText={'Carregando...'}
                    notFoundText={'Não encontrado'}
                  />

                  <Autocomplete
                    name='place'
                    minLength={1}
                    keyField='id'
                    label="Local"
                    required={true}
                    hideErrorLabel={true}
                    disabled={previewMode}
                    value={values && values.place}
                    valueFormat={(row) => `${row.name}`}
                    loadData={actions.listPlaces}
                    emptyText={'Pesquise um local'}
                    tipText={'Digite... '}
                    loadingText={'Carregando...'}
                    notFoundText={'Não encontrado'}
                    />

                  <Autocomplete
                    name='manager'
                    minLength={1}
                    keyField='id'
                    label="Gerente"
                    required={true}
                    hideErrorLabel={true}
                    disabled={previewMode}
                    value={values && values.manager}
                    valueFormat={(row) => `${row.name}`}
                    loadData={actions.listUsers}
                    emptyText={'Pesquise um usuário'}
                    tipText={'Digite... '}
                    loadingText={'Carregando...'}
                    notFoundText={'Não encontrado'}
                    />
                </Row>
                <Row span={previewMode ? 5 : 4}>
                  {previewMode &&
                    <Select
                      name="status"
                      label="Fase"
                      options={{ values: [
                        { value: 'accreditation', label: 'Credenciamento' },
                        { value: 'checklist', label: 'Checklist' },
                      ]}}
                      onChange={(evt) => eventActions.changeStatus(_.get(model, 'id'), evt.target.value)}
                      />
                  }

                  <InputDate
                    name="accreditationStartDate"
                    label="Data início do credenciamento"
                    required={true}
                    hideErrorLabel={true}
                    disabled={previewMode}
                    />

                  <InputDate
                    name="accreditationEndDate"
                    label="Data final do credenciamento"
                    required={true}
                    hideErrorLabel={true}
                    disabled={previewMode}
                    />

                  <InputDate
                    name="eventDate"
                    label="Data do evento"
                    required={true}
                    hideErrorLabel={true}
                    withTime={true}
                    disabled={previewMode}
                    />

                  <InputDate
                    name="eventEndDate"
                    label="Data do término do evento"
                    required={true}
                    hideErrorLabel={true}
                    withTime={true}
                    disabled={previewMode}
                    />
                </Row>
              </div>

              <TableContainer>
                <div className='list'>
                  <FormHeader>
                    Fornecedores ({_.size(values.suppliers)})
                  </FormHeader>

                  <DataTable
                    columns={columns}
                    data={{ rows: getSuppliers(previewMode, model.allSuppliers, selecteds) }}
                    noPagination={true}
                    keyField="identify"
                    emptyText="Nenhum fornecedor adicionado"
                    extraOptions={{
                      ignoreRowClicked: true,
                      selectableRows: !previewMode,
                      selectableRowsHighlight: false,
                      selectableRowSelected: (row) => {
                        return selecteds.includes(row.id);
                      },
                      onSelectedRowsChange: ({ allSelected, selectedCount, selectedRows }) => {
                        if (!previewMode) {
                          actions.selectSuppliers(selectedRows, values);
                        }
                      }
                    }}
                  />
                </div>
              </TableContainer>

              <div className='toolbar'>
                <IconButton title="Cancelar" disabled={false} onClick={() => history.push('/events')}>
                  <MdKeyboardBackspace />
                </IconButton>

                {isAccreditation &&
                  <IconButton
                    title="Adicionar novo colaborador"
                    onClick={actions.openSupplierModal}>
                    <MdAdd />
                  </IconButton>
                }

                {!previewMode &&
                  <IconButton
                    title="Salvar"
                    disabled={!isValid}
                    onClick={() => actions.createOrUpdate(values)}>
                    <MdSave />
                  </IconButton>
                }
              </div>
            </div>
          )
        }}
      </Formik>

      <SupplierModal
        loading={state.modalLoading}
        data={state.modalData || {}}
        handleOnSubmit={actions.submitNewSupplier}
        isOpen={state.showSupplierModal}
        closeModal={actions.closeSupplierModal}
        onListSuppliers={actions.listSuppliers}
        title={<>Incluir Fornecedor</>}
        />
    </CreateUpdateContainer>
  );
}

export default CreateOrUpdate;